import { Injectable } from '@angular/core';
import { environment } from '@root/src/environments/environment';
import * as Sentry from '@sentry/angular';
import { FeatureFlagService } from './feature-flag.service';
import {
    catchError, firstValueFrom, map, of
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { BASE_PATH_API } from '@manager-base/app-base/environment/paths/constants';

@Injectable({
    providedIn: 'root'
})
// adapted from https://stackoverflow.com/questions/34489916/how-to-load-external-scripts-dynamically-in-angular
export class SentryLoaderService {
    constructor(private flagService: FeatureFlagService) {}

    loadSentry(): void {
        if (environment.sentry.enabled === 'true') {
            Sentry.init({
                dsn: environment.sentry.dsn,
                environment: environment.sentry.environment,
                release: environment.sentry.release,
                beforeSend: (event, hint) => {
                    this.handleInfinteDigestErrorEvent(event);

                    return this.isFlagEnabled(this.handleNonErrorEvent(event, hint), this.flagService);
                },
                beforeBreadcrumb: (breadcrumb) => {
                    if (this.isApiBreadcrumb(breadcrumb)) {
                        return null;
                    }

                    if (this.isNewRelicBreadcrumb(breadcrumb)) {
                        return null;
                    }

                    return breadcrumb;
                },
            });
        }
    }

    private isFlagEnabled(event: Sentry.ErrorEvent, flagService: FeatureFlagService): PromiseLike<Sentry.ErrorEvent | null> {
        const getEvent$ = flagService.retrievePublicFlag(this.flagService.keys.UseSentryForManagerClient).pipe(
            map(isSentryEnabled => {
                return isSentryEnabled ? event : null;
            }),
            catchError(() => of(null))
        );

        return firstValueFrom(getEvent$);
    }

    private isApiBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
        return breadcrumb.type === 'http'
            && breadcrumb.data
            && (breadcrumb.data.status_code === 200 || breadcrumb.data.status_code === 204)
            && breadcrumb.data.url
            && (breadcrumb.data.url.toString().includes(`${BASE_PATH_API}`));
    }

    private isNewRelicBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
        return breadcrumb.type === 'http'
            && breadcrumb.data
            && breadcrumb.data.url
            && (breadcrumb.data.url.toString().includes('bam.nr-data.net'));
    }

    private handleNonErrorEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent {
        if (hint.originalException instanceof HttpErrorResponse
            && event.exception && event.exception.values && event.exception.values.length
            && event.exception.values[0].value && event.exception.values[0].value.startsWith('Non-Error exception captured')
        ) {
            // Http error reponse was captured outside of an area using the global error handler.
            event.exception.values[0].value = hint.originalException.message;
        }

        return event;
    }

    // When running in hybrid mode, Angular's ngUpgrade manages change detection between both frameworks - Angular zone and AngularJS $digest.
    // [$rootScope:infdig] Infinite $digest Loop errors can occur when a digest was triggered while one was already in progress.
    // This could mean that something is triggering a digest outside of either framework's change detection.
    private handleInfinteDigestErrorEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
        const isInfDigErrorWithUrl = !!(event.exception && event.exception.values && event.exception.values.length && event.exception.values[0].value
            && event.exception.values[0].value.includes('[$rootScope:infdig]'))
            && !!(event && event.request && event.request.url);

        if (isInfDigErrorWithUrl) {
            // We can control the event grouping using the Sentry SDK
            // https://docs.sentry.io/platforms/javascript/guides/angular/enriching-events/fingerprinting/
            event.fingerprint = ['{{ default }}'];

            // This AngularJS "Update task" modal uses a downgraded Angular component - <bdx-quill-mention-form-element>.
            // <bdx-quill-mention-form-element> uses ngx-quill to help integrate Quill with Angular, so events are fired outside of the Angular zone (change detection).
            // For some navigation paths to this modal, we get an inifite $digest loop error when Quill is invoked.
            if (isInfDigErrorWithUrl && (event.request.url.toString().includes('/task-summary') || event.request.url.toString().includes('/tasks/list'))) {
                event.fingerprint.push('Tasks area');
            }

            // These events don't have repro steps, but we can group them based on location
            if (isInfDigErrorWithUrl && (event.request.url.toString().includes('/properties/add'))) {
                event.fingerprint.push('Properties/add area');
            }

            // These events don't have repro steps, but we can group them based on location
            if (isInfDigErrorWithUrl && (event.request.url.toString().endsWith('/manager/app/'))) {
                event.fingerprint.push('App init area');
            }
        }

        return event;
    }
}
